/**
 * Advance Popup — one-time welcome gift form for new visitors.
 *
 * Shows once per browser (localStorage). Submits the visitor to Shopify via the
 * built-in contact form and/or the customer (marketing) form, then reveals a
 * coupon code taken from the section settings.
 */
if (!customElements.get('advance-popup')) {
  class AdvancePopup extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.delay = parseInt(this.dataset.delay, 10) * 1000;
      this.storageKey = this.dataset.storageKey || 'advance-popup';
      this.remindAfter = parseInt(this.dataset.remindAfter, 10) || 0;
      this.designMode = this.dataset.designMode === 'true';
      this.exitIntent = this.dataset.exitIntent === 'true';

      this.form = this.querySelector('[data-advance-popup-form]');
      this.formError = this.querySelector('[data-advance-popup-formerror]');
      this.submit = this.querySelector('[data-advance-popup-submit]');
      this.submitLabel = this.submit ? this.submit.textContent : '';
      this.couponEl = this.querySelector('[data-advance-popup-coupon]');
      this.copyButton = this.querySelector('[data-advance-popup-copy]');

      this.querySelectorAll('[data-advance-popup-close]').forEach((button) => {
        button.addEventListener('click', this.dismiss.bind(this));
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.dismiss();
      });
      document.addEventListener('keyup', (event) => {
        if (this.hasAttribute('open') && event.key === 'Escape') this.dismiss();
      });

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.copyButton) this.copyButton.addEventListener('click', this.copyCode.bind(this));

      if (this.designMode) {
        this.bindDesignMode();
        return;
      }

      if (this.shouldShow()) this.schedule();
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    /* ---------------------------------------------------------- visibility */

    getState() {
      try {
        return JSON.parse(window.localStorage.getItem(this.storageKey)) || null;
      } catch (error) {
        return null;
      }
    }

    setState(status) {
      try {
        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({ status: status, at: Date.now() })
        );
      } catch (error) {
        // Storage unavailable (private mode) — the popup simply shows again.
      }
    }

    shouldShow() {
      const state = this.getState();
      if (!state) return true;
      // Someone who already claimed the gift never sees it again.
      if (state.status === 'submitted') return false;
      if (!this.remindAfter) return false;
      const elapsed = Date.now() - (state.at || 0);
      return elapsed > this.remindAfter * 86400000;
    }

    schedule() {
      if (this.exitIntent) {
        this.onExitIntent = (event) => {
          if (event.clientY > 0) return;
          document.removeEventListener('mouseleave', this.onExitIntent);
          this.show();
        };
        document.addEventListener('mouseleave', this.onExitIntent);
      }
      setTimeout(() => this.show(), this.delay);
    }

    show() {
      if (this.hasAttribute('open')) return;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => this.classList.add('is-visible'));
      // Move focus into the dialog itself, never an input: focusing a field
      // scrolls the document and pops the keyboard open on mobile.
      setTimeout(() => {
        const dialog = this.querySelector('[role="dialog"]');
        if (dialog) dialog.focus({ preventScroll: true });
      }, 350);
    }

    hide() {
      this.classList.remove('is-visible');
      document.body.classList.remove('overflow-hidden');
      setTimeout(() => this.removeAttribute('open'), 300);
    }

    dismiss() {
      if (!this.designMode && !this.classList.contains('is-success')) this.setState('dismissed');
      this.hide();
    }

    bindDesignMode() {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.sectionId) this.show();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.sectionId) this.hide();
      });
      this.show();
    }

    /* -------------------------------------------------------------- submit */

    validate() {
      let valid = true;
      this.form.querySelectorAll('[required]').forEach((input) => {
        const field =
          input.closest('.advance-popup__field') || input.closest('.advance-popup__consent');
        const ok = input.checkValidity();
        input.setAttribute('aria-invalid', ok ? 'false' : 'true');
        if (field) field.classList.toggle('has-error', !ok);
        if (!ok && valid) input.focus({ preventScroll: true });
        if (!ok) valid = false;
      });
      return valid;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.validate()) return;

      this.formError.classList.remove('is-active');
      this.submit.disabled = true;
      this.submit.textContent = this.submit.dataset.loadingLabel || 'Please wait...';

      const value = (field) => {
        const input = this.form.querySelector('[name="' + field + '"]');
        return input ? input.value.trim() : '';
      };
      const data = { name: value('name'), email: value('email'), phone: value('phone') };

      try {
        const requests = [];
        if (this.dataset.sendContact === 'true') requests.push(this.postContact(data));
        if (this.dataset.sendCustomer === 'true') requests.push(this.postCustomer(data));
        if (this.dataset.webhook) requests.push(this.postWebhook(data));

        const results = await Promise.allSettled(requests);
        const delivered = results.some((result) => result.status === 'fulfilled');

        if (!requests.length || delivered) {
          this.onSuccess(data);
        } else {
          this.onError();
        }
      } catch (error) {
        this.onError();
      }
    }

    postContact(data) {
      const body = new FormData();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[name]', data.name);
      body.append('contact[email]', data.email);
      body.append('contact[phone]', data.phone);
      body.append('contact[body]', this.dataset.contactBody || 'Welcome gift popup signup');
      return this.post('/contact', body);
    }

    postCustomer(data) {
      const parts = data.name.split(' ');
      const body = new FormData();
      body.append('form_type', 'customer');
      body.append('utf8', '✓');
      body.append('contact[tags]', this.dataset.customerTags || 'newsletter');
      body.append('contact[email]', data.email);
      body.append('contact[first_name]', parts.shift() || '');
      body.append('contact[last_name]', parts.join(' '));
      if (data.phone) body.append('contact[phone]', data.phone);
      return this.post('/contact', body);
    }

    postWebhook(data) {
      return fetch(this.dataset.webhook, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          coupon: this.resolveCode(),
          source: 'advance-popup',
          page: window.location.href
        })
      });
    }

    post(url, body) {
      return fetch(url, {
        method: 'POST',
        body: body,
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response;
      });
    }

    onError() {
      this.submit.disabled = false;
      this.submit.textContent = this.submitLabel;
      this.formError.classList.add('is-active');
    }

    onSuccess(data) {
      this.setState('submitted');
      if (this.couponEl) this.couponEl.textContent = this.resolveCode();
      this.classList.add('is-success');
      this.querySelector('.advance-popup__body').scrollTop = 0;

      document.dispatchEvent(
        new CustomEvent('advance-popup:submitted', { detail: { email: data.email } })
      );
    }

    /* -------------------------------------------------------------- coupon */

    /** Fixed code, or one drawn at random from the code pool and then kept. */
    resolveCode() {
      if (this.assignedCode) return this.assignedCode;

      const stored = window.localStorage.getItem(this.storageKey + ':code');
      if (stored) {
        this.assignedCode = stored;
        return stored;
      }

      const pool = (this.dataset.couponPool || '')
        .split('\n')
        .map((code) => code.trim())
        .filter(Boolean);

      this.assignedCode = pool.length
        ? pool[Math.floor(Math.random() * pool.length)]
        : this.dataset.couponCode || '';

      try {
        window.localStorage.setItem(this.storageKey + ':code', this.assignedCode);
      } catch (error) {
        // Non-fatal.
      }
      return this.assignedCode;
    }

    copyCode() {
      const code = this.couponEl ? this.couponEl.textContent.trim() : '';
      if (!code) return;
      const done = () => {
        const original = this.copyButton.dataset.label || this.copyButton.textContent;
        this.copyButton.dataset.label = original;
        this.copyButton.textContent = this.copyButton.dataset.copiedLabel || 'Copied!';
        setTimeout(() => {
          this.copyButton.textContent = original;
        }, 2000);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done);
      } else {
        const helper = document.createElement('textarea');
        helper.value = code;
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.select();
        document.execCommand('copy');
        document.body.removeChild(helper);
        done();
      }
    }
  }
  customElements.define('advance-popup', AdvancePopup);
}
